import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from 'layout/components/ErrorBoundary';

import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import { readStoredInputMethod } from 'settings/utils/inputMethodStorage';
import { useGuidanceQuestions } from 'priorities/hooks/useGuidanceQuestions';

import * as Styled from 'shiftNotes/styles/InlineNoteCapture.style';

interface InlineNoteCaptureProps {
  eventId?: string;
  canResolve?: boolean;
}

export default function InlineNoteCapture({
  eventId,
  canResolve = false,
}: InlineNoteCaptureProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();
  const {
    isRecording,
    isWritingMode,
    showCreatedCard,
    mediaError,
    openWritingMode,
    onRecordingToggle,
    clearActiveGuidanceQuestion,
    enterResolveMode,
    exitResolveMode,
  } = useShiftNoteCapture();

  const { data: guidanceQuestions } = useGuidanceQuestions();
  const priorityQuestion = useMemo(() => {
    if (!eventId) return null;
    return (
      guidanceQuestions?.find((q) => q.event === eventId && !q.is_answered) ??
      null
    );
  }, [eventId, guidanceQuestions]);
  const priorityPrompt = priorityQuestion?.text ?? null;

  const isReady =
    !isRecording && !isWritingMode && !showCreatedCard && !mediaError;

  if (!isReady) return null;

  const handleOpenNoteCapture = () => {
    const guidanceQuestionId = priorityQuestion?.id;
    exitResolveMode();
    if (!guidanceQuestionId) {
      clearActiveGuidanceQuestion();
    }
    if (readStoredInputMethod() === 'keyboard') {
      shiftNotesCapture.captureInlineWritingOpened();
      openWritingMode(guidanceQuestionId);
      return;
    }
    onRecordingToggle(guidanceQuestionId);
  };

  const handleOpenResolve = () => {
    shiftNotesCapture.captureInlineResolveOpened();
    enterResolveMode();
    if (readStoredInputMethod() === 'keyboard') {
      openWritingMode();
      return;
    }
    onRecordingToggle();
  };

  const hasPriority = !!priorityPrompt;

  return (
    <ErrorBoundary>
      <Styled.Container
        data-testid="inline-note-capture"
        $hasPriority={hasPriority}
      >
        {hasPriority && (
          <Styled.PriorityPromptWrapper>
            <Styled.PriorityPrompt data-testid="inline-note-capture-priority-prompt">
              {priorityPrompt}
            </Styled.PriorityPrompt>
          </Styled.PriorityPromptWrapper>
        )}
        <Styled.ActionsRow>
          <Styled.AddUpdateButton
            $size="medium"
            stretch
            onClick={handleOpenNoteCapture}
            data-testid="inline-note-capture-add-update-button"
          >
            {t('inlineNoteCapture.addUpdate')}
          </Styled.AddUpdateButton>
          {canResolve && (
            <Styled.ResolveButton
              $size="medium"
              stretch
              onClick={handleOpenResolve}
              data-testid="inline-note-capture-resolve-button"
            >
              {t('inlineNoteCapture.resolve')}
            </Styled.ResolveButton>
          )}
        </Styled.ActionsRow>
      </Styled.Container>
    </ErrorBoundary>
  );
}
